import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore as useReduxStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getSearchPageName } from '@/pages/Search/helpers/getSearchPageName';
import { type LoadZeroResultsDataParams, loadZeroResultsData } from '@/pages/Search/helpers/loadZeroResultsData';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { setIsZeroResults, submitSearch } from '@/redux/modules/search/search.actions';
import { getSearchCorrectedTerm, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { GlobalState } from '@/redux/store';
import { SearchQuery } from '@/types/search/searchQuery';
import { isArchivedSearch } from '@/utils/queryParams';

type UseHandleRefetchOnExcludedHouseParams = Pick<LoadZeroResultsDataParams, 'browseFacetOptionIds' | 'queryParams'> & {
    searchQuery: SearchQuery;
};

export const useHandleRefetchOnExcludedHouse = ({
    browseFacetOptionIds,
    queryParams,
    searchQuery,
}: UseHandleRefetchOnExcludedHouseParams) => {
    const userExcludedHouses = useAppSelector(getUserExcludedHouses);
    const [initialHouses, setInitialHouses] = useState<number[]>(userExcludedHouses);
    const dispatch = useAppDispatch();
    const store = useReduxStore<GlobalState>();
    const { key: locationKey, pathname } = useLocation();
    const pageSize = useAppSelector(getSearchPageSize);
    const { submitted: isLoadingSearchResults } = useAppSelector(getSearchUiState);
    const pageName = getSearchPageName({ archivedSearch: isArchivedSearch(queryParams, pathname), pathname });

    // Read through a ref: these change identity on every navigation and would re-create `submitNewSearch`.
    const currentPageRef = useRef({ locationKey, pageName, pathname, queryParams });
    currentPageRef.current = { locationKey, pageName, pathname, queryParams };
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const submitNewSearch = useCallback(async () => {
        const { getState } = store;
        const { isZeroResults } = getSearchUiState(getState());
        const { locationKey: startedAtLocationKey, pageName, pathname, queryParams } = currentPageRef.current;

        await dispatch(submitSearch({ pageName, searchQuery }));

        // A navigation or unmount during the search means a newer search owns the page; going on would
        // run the past-results search over it.
        const isSuperseded = !isMountedRef.current || currentPageRef.current.locationKey !== startedAtLocationKey;
        if (!isZeroResults || isSuperseded) {
            return;
        }

        // The page shows past results the upcoming search can't return, so rebuild it the way the loader does.
        const { totalFound, totalSoldItems } = getSearchUiState(getState());
        if (totalFound > 0 || totalSoldItems === 0) {
            await dispatch(setIsZeroResults(false));
            return;
        }

        await loadZeroResultsData({
            browseFacetOptionIds,
            correctedSearchTerm: getSearchCorrectedTerm(getState(), searchQuery.searchTerm),
            dispatch,
            getState,
            pageName,
            pageSize,
            pathname,
            queryParams,
            shouldPreloadSoldItems: true,
        });
    }, [browseFacetOptionIds, dispatch, pageSize, searchQuery, store]);

    // Track initial houses on mount
    useEffect(() => {
        setInitialHouses(userExcludedHouses);
    }, [userExcludedHouses]);

    // Compare current houses with initial houses
    useEffect(() => {
        const initialSet = new Set(initialHouses);
        const currentSet = new Set(userExcludedHouses);
        const hasChanged =
            initialHouses.length !== userExcludedHouses.length ||
            initialHouses.some((id) => !currentSet.has(id)) ||
            userExcludedHouses.some((id) => !initialSet.has(id));

        if (hasChanged && !isLoadingSearchResults) {
            submitNewSearch();
        }
    }, [initialHouses, isLoadingSearchResults, submitNewSearch, userExcludedHouses]);
};
